import GrowthRecords from "@/features/Animals/GrowthRecords";
import { FarmerAnimalProfile } from "@/features/Animals/Farmer.Animal.Profile.tsx";
import { useGetAnimal } from "@/features/Animals/animalApiSlice.ts";
import { useParams } from "react-router-dom";
import {  useRef } from "react";
import { EnhancedPdfGenerator } from "@/pages/Profile.tsx";

// @ts-ignore
const html2pdf = (await import("html2pdf.js")).default as any;

export const AnimalProfilePage = () => {
  const { id } = useParams();
  const { data } = useGetAnimal(id);
  const contentRef = useRef<HTMLDivElement>(null);

  const generatePDF = 
    async (options: {
      includeImages: boolean;
      includeHealthRecords: boolean;
      includePedigree: boolean;
      includeOwnerInfo: boolean;
      highQuality: boolean;
    }) => {
      // reactToPrintFn();

      if (!contentRef.current) {
        console.error("Could not find overview section");
        return;
      }

      try {
        // Clone the section to modify for PDF
        const element = contentRef.current.cloneNode(true) as HTMLElement;

        // Remove interactive elements
        const buttonsToRemove = element.querySelectorAll(
          ".flex.flex-col.sm\\:flex-row.gap-4.justify-end",
        );
        buttonsToRemove.forEach((btn) => btn.remove());

        // Conditionally remove sections
        if (!options.includeOwnerInfo) {
          const cardTitles = element.querySelectorAll('[class*="CardTitle"]');
          let ownerSection: any = null;
          cardTitles.forEach((title) => {
            if (title.textContent?.includes("Breeder/Owner")) {
              ownerSection = title.closest(".mt-4");
            }
          });
          if (ownerSection) ownerSection.remove();
        }

        if (!options.includeHealthRecords) {
          const cardTitles = element.querySelectorAll('[class*="CardTitle"]');
          let healthSection: any = null;
          cardTitles.forEach((title) => {
            if (title.textContent?.includes("Recent Health")) {
              healthSection = title.closest('div[class*="Card"]');
            }
          });
          if (healthSection) healthSection.remove();
        }

        if (!options.includePedigree) {
          const cardTitles = element.querySelectorAll('[class*="CardTitle"]');
          let breedingSection: any = null;
          cardTitles.forEach((title) => {
            if (title.textContent?.includes("Breeding Information")) {
              breedingSection = title.closest('div[class*="Card"]');
            }
          });
          if (breedingSection) breedingSection.remove();
        }

        // Handle images based on options
        if (!options.includeImages) {
          const images = element.querySelectorAll("img");
          images.forEach((img, i) => {
            console.log(
              `debug Image ${i}: src=${img.src}, complete=${img.complete}`,
            );

            const placeholder = document.createElement("div");
            placeholder.style.width = "100%";
            placeholder.style.height = "100%";
            placeholder.style.backgroundColor = "#f0f0f0";
            placeholder.style.display = "flex";
            placeholder.style.alignItems = "center";
            placeholder.style.justifyContent = "center";
            placeholder.textContent = "Image placeholder";
            img.parentNode?.replaceChild(placeholder, img);
          });
        } else {
          // Force all images to have crossOrigin attribute
          const images = element.querySelectorAll("img");
          images.forEach((img) => {
            img.crossOrigin = "anonymous";

            // If using relative URLs, convert them to absolute
            if (
              img.src &&
              !img.src.startsWith("http") &&
              !img.src.startsWith("data:")
            ) {
              img.src = new URL(img.src, window.location.origin).href;
            }

            // Add a small delay to ensure images are processed
            img.style.maxWidth = "100%";
            img.style.height = "auto";
          });
        }

        // Add a title at the top
        const titleDiv = document.createElement("div");
        titleDiv.innerHTML = `
        <div style="width: 100%; max-width: 1200px: ; text-align: center; margin: "0 auto"; padding: 20px; border-bottom: 1px solid #e5e7eb;">
          <h1 style="font-size: 28px; font-weight: bold; margin-bottom: 8px;">${data?.name} - Animal Profile</h1>
          <p style="font-size: 16px; color: #6b7280;">${data?.racialComposition ?? data?.breed} | ${data?.sex} | Born: ${new Date(
            data?.birthDate,
          ).toLocaleDateString()}</p>
          <p style="font-size: 14px; color: #9ca3af; margin-top: 8px;">Generated on ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
        </div>
      `;
        element.insertBefore(titleDiv, element.firstChild);

        // Pre-load all images before generating PDF
        const images = element.querySelectorAll("img");
        console.log(`Found ${images.length} images to process`);

        // Wait for all images to load or timeout after 3
        // seconds
        const imagePromises = Array.from(images).map((img) => {
          if (img.complete || !img.src) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve; // Resolve even on error to
            // avoid blocking
            // Ensure we load a fresh copy of the image
            if (img.src.indexOf("?") === -1) {
              img.src = img.src + "?" + new Date().getTime();
            }
          });
        });

        await Promise.race([
          Promise.all(imagePromises),
          new Promise((resolve) => setTimeout(resolve, 3000)), // 3s timeout
        ]);

        console.log("Images processed, generating PDF");

        // Set options for the PDF
        const opt = {
          margin: [20, 20, 20, 20],
          filename: `${data?.name.replace(/\s+/g, "-").toLowerCase()}-profile.pdf`,
          image: {
            type: "jpeg",
            quality: options.highQuality ? 0.95 : 0.8,
          },
          html2canvas: {
            scale: options.highQuality ? 4 : 2,
            useCORS: true,
            allowTaint: true, // Allow loading of tainted
            // images
            letterRendering: true,
            logging: true, // For debugging
            imageTimeout: 5000, // Longer timeout for images
          },
          jsPDF: {
            unit: "mm",
            format: "a2",
            orientation: "portrait",
            compress: true,
          },
        };

        // Generate the PDF
        await html2pdf().from(element).set(opt).save();

        console.log("PDF generated successfully");
      } catch (error) {
        console.error("Error generating PDF:", error);
        throw error;
      }
    }

  // const reactToPrintFn = useReactToPrint({ contentRef });

  return (
    <main className="min-h-screen w-screen py-8 flex flex-col">
      <div ref={contentRef} id={"pdf-content"} className={"w-full"}>
        <FarmerAnimalProfile animalData={data} className={""} />
      </div>
      {id && <GrowthRecords animalId={id} />}
      <EnhancedPdfGenerator
        animalName={data?.name ?? "Animal"}
        breed={data?.racialComposition ?? "unknown"}
        generatePdfFunction={generatePDF}
      />
    </main>
  );
};
